import React, { useState, useEffect } from 'react';
import { X, Eye, RefreshCw, Monitor, Smartphone, Tablet, Send, Check, AlertCircle } from 'lucide-react';
import { emailApi } from '../../lib/api/email';

interface TemplatePreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    templateId?: string;
    templateName?: string;
    htmlContent?: string;
    subject?: string;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
    isOpen,
    onClose,
    templateId,
    templateName,
    htmlContent: initialHtml,
    subject: initialSubject
}) => {
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
    const [testData, setTestData] = useState<Record<string, string>>({
        userName: 'John Doe',
        email: 'john@example.com',
        appName: 'Dashbal',
        resetUrl: 'https://dashbal.com/reset',
        verificationUrl: 'https://dashbal.com/verify'
    });
    const [showVariables, setShowVariables] = useState(false);
    const [deviceMode, setDeviceMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
    const [showTestSend, setShowTestSend] = useState(false);
    const [testRecipient, setTestRecipient] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [testSent, setTestSent] = useState(false);
    const [testError, setTestError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            if (templateId) {
                // Fetch preview from API
                loadPreview();
            } else if (initialHtml) {
                // Use provided HTML directly
                setPreview({
                    subject: renderVariables(initialSubject || 'Preview'),
                    html: renderVariables(initialHtml)
                });
            }
        }
    }, [isOpen, templateId, initialHtml, initialSubject]);

    const renderVariables = (template: string): string => {
        if (!template) return '';
        return template.replace(/\{\{([\w.]+)\}\}/g, (match, key) => {
            return testData[key] || match;
        });
    };

    const loadPreview = async () => {
        if (!templateId) return;
        setLoading(true);
        try {
            const result = await emailApi.previewTemplate(templateId, testData);
            setPreview(result);
        } catch (error) {
            console.error('Failed to load preview:', error);
            // Fallback to client-side rendering
            if (initialHtml) {
                setPreview({
                    subject: renderVariables(initialSubject || 'Preview'),
                    html: renderVariables(initialHtml)
                });
            }
        }
        setLoading(false);
    };

    const handleVariableChange = (key: string, value: string) => {
        setTestData(prev => ({ ...prev, [key]: value }));
    };

    const refreshPreview = () => {
        if (templateId) {
            loadPreview();
        } else if (initialHtml) {
            setPreview({
                subject: renderVariables(initialSubject || 'Preview'),
                html: renderVariables(initialHtml)
            });
        }
    };

    const handleSendTest = async () => {
        if (!testRecipient || !preview) return;
        setSendingTest(true);
        setTestError(null);
        try {
            await emailApi.sendTestEmail({
                to: testRecipient,
                subject: preview.subject,
                html: preview.html
            });
            setTestSent(true);
            setTimeout(() => {
                setTestSent(false);
                setShowTestSend(false);
            }, 3000);
        } catch (error: any) {
            console.error('Failed to send test email:', error);
            setTestError(error.message || 'Failed to send test email');
        }
        setSendingTest(false);
    };

    const getPreviewWidth = () => {
        switch (deviceMode) {
            case 'mobile': return '375px';
            case 'tablet': return '768px';
            default: return '100%';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-neutral-50">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-[#c7613d]/10 flex items-center justify-center text-[#c7613d] shadow-sm">
                            <Eye size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-display font-bold text-neutral-900 leading-tight">Email Preview</h2>
                            {templateName && (
                                <p className="text-sm text-neutral-500 mt-0.5">{templateName}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center bg-neutral-200/50 rounded-xl p-1">
                            <button
                                onClick={() => setDeviceMode('desktop')}
                                className={`p-2 rounded-md transition-all ${deviceMode === 'desktop' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`}
                                title="Desktop"
                            >
                                <Monitor size={18} />
                            </button>
                            <button
                                onClick={() => setDeviceMode('tablet')}
                                className={`p-2 rounded-md transition-all ${deviceMode === 'tablet' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`}
                                title="Tablet"
                            >
                                <Tablet size={18} />
                            </button>
                            <button
                                onClick={() => setDeviceMode('mobile')}
                                className={`p-2 rounded-md transition-all ${deviceMode === 'mobile' ? 'bg-white shadow-sm text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`}
                                title="Mobile"
                            >
                                <Smartphone size={18} />
                            </button>
                        </div>

                        <div className="h-6 w-px bg-neutral-200" />

                        <button
                            onClick={() => setShowVariables(!showVariables)}
                            className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-all shadow-sm ${showVariables
                                ? 'bg-neutral-900 text-white'
                                : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                                }`}
                        >
                            {showVariables ? 'Hide Variables' : 'Edit Variables'}
                        </button>

                        <div className="relative">
                            <button
                                onClick={() => setShowTestSend(!showTestSend)}
                                className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-all shadow-sm flex items-center gap-2 ${showTestSend
                                    ? 'bg-[#c7613d] text-white'
                                    : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                                    }`}
                            >
                                <Send size={14} />
                                Send Test
                            </button>

                            {showTestSend && (
                                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-neutral-200 p-4 z-50 animate-in fade-in slide-in-from-top-2">
                                    <h4 className="text-xs font-bold text-neutral-900 uppercase tracking-wide mb-3">Send Test Email</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <input
                                                type="email"
                                                placeholder="Enter recipient email..."
                                                value={testRecipient}
                                                onChange={(e) => setTestRecipient(e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:ring-2 focus:ring-[#c7613d]/20 focus:border-[#c7613d] outline-none transition-all"
                                                autoFocus
                                            />
                                        </div>
                                        {testError && (
                                            <div className="flex items-center gap-2 text-red-600 text-[10px] bg-red-50 p-2 rounded-lg">
                                                <AlertCircle size={12} />
                                                <span>{testError}</span>
                                            </div>
                                        )}
                                        <button
                                            onClick={handleSendTest}
                                            disabled={sendingTest || !testRecipient || !preview}
                                            className={`w-full py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${testSent
                                                ? 'bg-green-500 text-white'
                                                : 'bg-[#c7613d] text-white hover:bg-[#b05536] disabled:opacity-50 disabled:cursor-not-allowed'
                                                }`}
                                        >
                                            {sendingTest ? (
                                                <RefreshCw size={14} className="animate-spin" />
                                            ) : testSent ? (
                                                <>
                                                    <Check size={14} />
                                                    Sent!
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={14} />
                                                    Send Now
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={refreshPreview}
                            disabled={loading}
                            className="p-2.5 hover:bg-neutral-100 border border-transparent hover:border-neutral-200 rounded-xl transition-colors"
                            title="Refresh Preview"
                        >
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        </button>

                        <button
                            onClick={onClose}
                            className="p-2.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Variables Sidebar */}
                    {showVariables && (
                        <div className="w-80 border-r border-neutral-200 p-6 overflow-y-auto bg-white flex-shrink-0">
                            <h3 className="text-xs font-bold text-neutral-900 uppercase tracking-wide mb-4">Test Data</h3>
                            <div className="space-y-4">
                                {Object.entries(testData).map(([key, value]) => (
                                    <div key={key}>
                                        <label className="text-xs text-neutral-500 font-medium block mb-1.5 uppercase">{'{{' + key + '}}'}</label>
                                        <input
                                            type="text"
                                            value={value}
                                            onChange={(e) => handleVariableChange(key, e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:ring-2 focus:ring-[#c7613d]/20 focus:border-[#c7613d] outline-none transition-all"
                                        />
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={refreshPreview}
                                className="mt-6 w-full py-2.5 text-sm font-medium bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/10"
                            >
                                Apply & Refresh
                            </button>
                        </div>
                    )}

                    {/* Preview Content */}
                    <div className="flex-1 bg-neutral-100 relative overflow-hidden flex flex-col">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-full">
                                <RefreshCw size={48} className="animate-spin text-neutral-300 mb-4" />
                                <p className="text-neutral-500 font-medium">Rendering preview...</p>
                            </div>
                        ) : preview ? (
                            <div className="flex-1 flex flex-col h-full">
                                {/* Subject Bar */}
                                <div className="bg-white border-b border-neutral-200 px-6 py-3 flex items-center gap-3 shadow-sm z-10">
                                    <span className="text-xs font-bold text-neutral-500 uppercase tracking-wide">Subject:</span>
                                    <span className="font-medium text-neutral-900">{preview.subject}</span>
                                </div>

                                {/* Iframe Container */}
                                <div className="flex-1 overflow-auto p-8 flex justify-center">
                                    <div
                                        className="bg-white shadow-2xl transition-all duration-300 ease-in-out"
                                        style={{
                                            width: getPreviewWidth(),
                                            height: '100%',
                                            maxWidth: '100%',
                                            borderRadius: deviceMode === 'desktop' ? '0' : '16px',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <iframe
                                            srcDoc={preview.html}
                                            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                                            title="Email Preview"
                                            sandbox="allow-same-origin"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-neutral-400">
                                <Eye size={64} className="mb-6 opacity-20" />
                                <p className="text-lg font-medium">No preview available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TemplatePreviewModal;
